import { c as _c } from "react/compiler-runtime";
import { ActionIcon, Badge, Button, Menu, Modal, Skeleton, TextInput, Tooltip, useSafeMantineTheme, Loader, CloseButton } from '@mantine/core';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useNavigationLoading } from 'components/hooks/useNavigationLoading';
import { HiChevronDown, HiClipboardCheck, HiCog, HiLightBulb, HiLogout, HiMenu, HiQuestionMarkCircle, HiChat, HiSelector } from 'react-icons/hi';
import Image from 'next/image';
import SwiftLogo from '@public/swift-logo.png';
import { IconContext } from 'react-icons';
import { WorkspaceSelect } from './Workspace';
import { Notifys } from 'components/notify/Notify';
import { nav, menuItems as menuItemsRaw, type MenuPermission } from './nav';
import { usePortal } from './portal';
import { SAvatar } from './SAvatar';
import { groupMap, isNullOrEmpty, notNullOrEmpty } from 'shared/collection';
import { uri } from 'shared/uri';
import { HrText, Masonry, MasonryGrid, Tip, useWindowDim } from 'shared/ui';
import { min } from 'd3-array';
import { FaHandSpock } from 'react-icons/fa';
import React, { ReactNode, useEffect, useState, useRef } from 'react';
import { ProfileForm, useAvatarSelection } from './Profile';
import { entries, groupBy, isEmpty, mapValues, partition, pick, range, sortBy, take, unique } from 'remeda';
import { AuthModal } from 'components/auth/AuthModal';
import { z } from 'zod';
import { useQueryString } from 'utils/useQueryString';
import { useTourContext } from './Tour';
import { match, P } from 'ts-pattern';
import { twMerge } from 'tailwind-merge';
import { zAuthActionQuery, zPortalQuery } from './portalTypes';
import { showError } from 'components/notify';
import { useMutation } from '@tanstack/react-query';
import { env } from 'utils/env';
import { doSafe } from 'shared/promise';
import { DateTime as D } from 'luxon';
import type { ProfileLicense } from './profileTypes';
import { Auth } from 'api/auth';
import { adminUserSections } from './admin/AdminUsers';
import { upperFirst } from '@mantine/hooks';
import { adminTabMd } from './admin/Admin';
import { Crisp } from 'crisp-sdk-web';
import { MdHome } from 'react-icons/md';
import { QuestionHeader, QuestionTileItems, QuestionSubquestions } from 'components/question/QuestionTile';
import { questionStatusMd, type QuestionStatus } from 'shared/forecast/questionStatus';
import { useQuestionSelect } from 'components/question/QuestionSelect';
import { MarkText } from 'components/form/MarkText';
import { QuestionAttachmentList } from 'components/question/QuestionAttachment';
import { QuestionStatusFilter } from 'components/question/QuestionStatus';
import type { FuseResult } from 'fuse.js';
import { Sticky } from 'components/Sticky';
import { ActionCard } from 'components/ActionPanel';
import type { NotNull } from 'shared/type';
import { TruncatedText } from 'components/TruncatedText';
import { useHotkeys } from '@mantine/hooks';
import { KbdComboLabel } from 'components/Keyboard';
import { NewQuestionButton, NewQuestionModal, useNewQModal } from 'components/question/QuestionNew';
import { ShareButton } from './ShareButton';
import { QuestionPageRow, useQuestionsPageQuery } from 'components/question/useQuestions';
export const TopBarMenuPortalSelector = '#top-bar-menu-portal';
const profileModalValues = ['open', 'welcome'] as const;
const isProfileModal = (value?: any): value is (typeof profileModalValues)[number] => value && profileModalValues.includes(value);
const showModalValues = [...profileModalValues, 'recovery', 'anon-signup'] as const;
const isShowModal = (value?: any): value is (typeof showModalValues)[number] => value && showModalValues.includes(value);
type ShowModal = (typeof showModalValues)[number];
const zQuery = z.object({
  ...zAuthActionQuery.shape,
  ...pick(zPortalQuery.shape, ['workspace', 'anon'])
});
const zGlobalNavQuery = z.object({
  workspace: z.string().optional()
}).strip();
const useTopBar = () => {
  const $ = _c(63);
  const router = useRouter();
  const portal = usePortal();
  const {
    updateProfile,
    profile,
    user,
    profiles,
    tz,
    workspace_id,
    noPrompt
  } = portal;
  const qs = useQueryString(zQuery);
  const qsGlobal = useQueryString(zGlobalNavQuery);
  const {
    query
  } = qs;
  const {
    authAction
  } = qs.query;
  const [modalOpen, setModalOpen] = useState(false);
  const {
    avatars
  } = useAvatarSelection();
  let t0;
  if ($[0] !== updateProfile) {
    t0 = t1 => {
      const {
        profile: profile_0,
        options
      } = t1;
      return updateProfile(profile_0, options);
    };
    $[0] = updateProfile;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== profile.id) {
    t1 = {
      id: profile.id
    };
    $[2] = profile.id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      mutationFn: t0,
      scope: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const updateProfileMutation = useMutation(t2);
  let t3;
  if ($[7] !== authAction || $[8] !== avatars || $[9] !== modalOpen || $[10] !== noPrompt || $[11] !== profile.name || $[12] !== profile.tz || $[13] !== qs || $[14] !== query || $[15] !== tz || $[16] !== updateProfileMutation || $[17] !== user) {
    t3 = () => {
      if (!!modalOpen) {
        return;
      }
      const provider = user?.app_metadata.provider;
      if (!isNullOrEmpty(authAction)) {
        qs.setQuery({
          authAction: undefined
        });
      }
      if (isShowModal(authAction) && provider == "email") {
        setModalOpen(authAction);
      } else {
        if (isNullOrEmpty(profile.name)) {
          if (!user.is_anonymous && !noPrompt) {
            setModalOpen("welcome");
          }
          if ((query.anon || user.is_anonymous) && avatars) {
            const randomAvatar = avatars[0];
            if (randomAvatar && !updateProfileMutation.isPending) {
              updateProfileMutation.mutateAsync({
                profile: {
                  ...pick(randomAvatar, ["avatar_url", "name"]),
                  tz
                },
                options: {
                  noNotify: true
                }
              });
            }
          }
        } else {
          if (profile.tz != tz && !updateProfileMutation.isPending) {
            updateProfileMutation.mutateAsync({
              profile: {
                tz
              },
              options: {
                noNotify: true
              }
            });
          }
        }
      }
    };
    $[7] = authAction;
    $[8] = avatars;
    $[9] = modalOpen;
    $[10] = noPrompt;
    $[11] = profile.name;
    $[12] = profile.tz;
    $[13] = qs;
    $[14] = query;
    $[15] = tz;
    $[16] = updateProfileMutation;
    $[17] = user;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const t4 = profiles?.global;
  let t5;
  if ($[19] !== authAction || $[20] !== avatars || $[21] !== profile || $[22] !== t4) {
    t5 = [authAction, t4, profile, avatars];
    $[19] = authAction;
    $[20] = avatars;
    $[21] = profile;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  useEffect(t3, t5);
  let t6;
  if ($[24] !== authAction || $[25] !== qs || $[26] !== query) {
    t6 = () => {
      if (authAction != "post-anon-signup") {
        return;
      }
      if (!query.auth_replacement_token || !query.auth_old_user_id) {
        showError("issue with anon user sign in (auth_replacement_token or auth_old_user_id missing), and the forecasts and other data wont be associated with this new account");
        return;
      }
      const props = {
        replacement_token: query.auth_replacement_token,
        old_user_id: query.auth_old_user_id
      };
      qs.setQuery({
        auth_replacement_token: undefined,
        auth_old_user_id: undefined
      });
      Auth.anonReplacement(props);
    };
    $[24] = authAction;
    $[25] = qs;
    $[26] = query;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== authAction) {
    t7 = [authAction];
    $[28] = authAction;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  useEffect(t6, t7);
  let t8;
  if ($[30] !== authAction || $[31] !== profile.workspace_id || $[32] !== qs || $[33] !== query?.workspace) {
    t8 = () => {
      if (!authAction && profile?.workspace_id != null && profile.workspace_id != query?.workspace) {
        qs.setQuery({
          workspace: profile.workspace_id
        });
      }
    };
    $[30] = authAction;
    $[31] = profile.workspace_id;
    $[32] = qs;
    $[33] = query?.workspace;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  const t9 = query?.workspace;
  const t10 = profile?.workspace_id;
  let t11;
  if ($[35] !== authAction || $[36] !== router.pathname || $[37] !== t10 || $[38] !== t9) {
    t11 = [router.pathname, t9, t10, authAction];
    $[35] = authAction;
    $[36] = router.pathname;
    $[37] = t10;
    $[38] = t9;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  useEffect(t8, t11);
  let t12;
  if ($[40] !== qs) {
    t12 = () => {
      if (qs.query.error_description) {
        showError(qs.query.error_description, "Auth error");
        qs.setQuery({
          error_description: undefined,
          error: undefined,
          error_code: undefined
        });
      }
    };
    $[40] = qs;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== qs.query.error_description) {
    t13 = [qs.query.error_description];
    $[42] = qs.query.error_description;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  useEffect(t12, t13);
  let t14;
  if ($[44] !== qsGlobal) {
    t14 = t15 => {
      const {
        path,
        params
      } = t15;
      return uri(path, zGlobalNavQuery).mergeQuery({
        ...qsGlobal.query,
        ...params
      });
    };
    $[44] = qsGlobal;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  const getNavUrl = t14;
  let t15;
  if ($[46] !== profile || $[47] !== profiles.global?.roles || $[48] !== qsGlobal || $[49] !== router.pathname || $[50] !== workspace_id) {
    const isGlobalAdmin = profiles.global?.roles.includes("admin");
    const hasPermissions = permissions => !permissions || permissions.every(p => p == "global-admin" ? isGlobalAdmin : profile.hasPermission(p));
    t15 = menuItemsRaw.filter(n => hasPermissions(n.permissions) && match(n).with({
      name: "feedback"
    }, () => profile.workspace.enableFeedback).with({
      name: "questions"
    }, _temp).otherwise(_temp2) && match(workspace_id).with("global", () => n.name == "admin").otherwise(_temp3)).map(n_0 => {
      const url = n_0.getUrl({
        ...qsGlobal.query
      });
      return {
        ...n_0,
        url,
        isActive: url.path?.join("/") == router.pathname || router.pathname == "/" && n_0.isHome,
        Icon: match(n_0.name).with("questions", _temp4).with("admin", _temp5).with("feedback", _temp6).with("accuracy", _temp7).otherwise(_temp8),
        subItems: doSafe(n_0.subItems, subs => entries(subs).filter(t16 => {
          const [, item] = t16;
          return hasPermissions(item.permissions);
        }).map(t17 => {
          const [id, item_0] = t17;
          return {
            id,
            ...item_0,
            Icon: match(n_0.name).with("admin", () => adminTabMd[id as keyof typeof adminTabMd]?.Icon).otherwise(_temp9),
            url: n_0.getUrl({
              ...qsGlobal.query,
              tab: id
            })
          };
        }))
      };
    });
    $[46] = profile;
    $[47] = profiles.global?.roles;
    $[48] = qsGlobal;
    $[49] = router.pathname;
    $[50] = workspace_id;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  const menuItems = t15;
  let t16;
  if ($[52] !== router.pathname || $[53] !== router.query) {
    const pathParts = router.pathname.split("/").filter(notNullOrEmpty);
    t16 = pathParts.at(0) == "question" ? router.query.slug?.[0] : undefined;
    $[52] = router.pathname;
    $[53] = router.query;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  const question_id = t16;
  let t17;
  if ($[55] !== getNavUrl || $[56] !== menuItems || $[57] !== modalOpen || $[58] !== noPrompt || $[59] !== portal || $[60] !== qs || $[61] !== question_id) {
    t17 = {
      portal,
      qs,
      modalOpen,
      setModalOpen,
      menuItems,
      noPrompt,
      getNavUrl,
      question_id
    };
    $[55] = getNavUrl;
    $[56] = menuItems;
    $[57] = modalOpen;
    $[58] = noPrompt;
    $[59] = portal;
    $[60] = qs;
    $[61] = question_id;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  return t17;
};
type UseTopBar = ReturnType<typeof useTopBar>;
type MenuItem = UseTopBar['menuItems'][number];
type MenuSubItem = NonNullable<MenuItem['subItems']>[number];
export const TopBar = t0 => {
  const $ = _c(111);
  const {
    className,
    ref
  } = t0;
  const {
    portal,
    qs,
    modalOpen,
    setModalOpen,
    menuItems,
    noPrompt,
    question_id
  } = useTopBar();
  const {
    query
  } = qs;
  const {
    colors
  } = useSafeMantineTheme();
  const {
    w
  } = useWindowDim();
  const inlineMenu = w >= 1000;
  const tour = useTourContext();
  const {
    profile,
    logOut,
    workspace: t1,
    user
  } = portal;
  const {
    advancedUI,
    name: workspaceName
  } = t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  useEffect(_temp12, t2);
  let t3;
  if ($[1] !== className) {
    t3 = twMerge("flex items-center justify-between gap-1 border-b pl-2 pr-4 w-full overflow-hidden", className);
    $[1] = className;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== inlineMenu) {
    t4 = inlineMenu && <Link href="/" className="flex place-items-center shrink-0"><Image alt="Swift logo" src={SwiftLogo} height={inlineMenu ? 30 : 30} /></Link>;
    $[3] = inlineMenu;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== inlineMenu || $[6] !== menuItems) {
    t5 = !inlineMenu && <HamburgerMenu menuItems={menuItems} />;
    $[5] = inlineMenu;
    $[6] = menuItems;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = doSafe(env.NEXT_PUBLIC_ENV_NAME, _temp13);
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = workspaceName.length > 25 ? "truncate" : "whitespace-nowrap";
  let t8;
  if ($[9] !== t7) {
    t8 = twMerge("text-lg font-semibold text-gray-700", t7);
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8) {
    t9 = <WorkspaceSelect className={t8} />;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== question_id || $[14] !== w) {
    t10 = w > 400 && <QuestionMenu question_id={question_id} showTitle={w > 800} />;
    $[13] = question_id;
    $[14] = w;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== inlineMenu || $[17] !== menuItems) {
    t11 = inlineMenu && <InlineMenu menuItems={menuItems} />;
    $[16] = inlineMenu;
    $[17] = menuItems;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11) {
    t12 = <nav className="grow self-stretch ml-2">{t11}</nav>;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== colors.gray[6]) {
    t13 = {
      size: "18px",
      color: colors.gray[6]
    };
    $[21] = colors.gray[6];
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Menu.Target><div className="flex items-center cursor-pointer"><HiQuestionMarkCircle /></div></Menu.Target>;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Menu width={300} shadow="md" position="bottom-end" trigger="click-hover" openDelay={100} closeDelay={400}>{t14}<Menu.Dropdown><Menu.Item component="a" href={nav.help.url.url} target="_new"><div className="flex items-center gap-2 font-semibold"><HiQuestionMarkCircle /><span>Help site</span></div><div className="text-sm opacity-60 mt-1">Get help about the app, or organize a call with forecast experts</div></Menu.Item>{env.NEXT_PUBLIC_CRISP_WEBSITE_ID && <Menu.Item onClick={_temp14}><div className="flex items-center gap-2 font-semibold"><HiChat color="var(--mantine-color-primary-5)" /><span>Chat</span></div><div className="text-sm opacity-60 mt-1">Get help about the app, or organize a workshop with forecast experts</div></Menu.Item>}</Menu.Dropdown></Menu>;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== w) {
    t16 = min([200, w]);
    $[25] = w;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== profile) {
    t17 = <SAvatar size="sm" profile={profile} hideTip={true} />;
    $[27] = profile;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <HiChevronDown />;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t17) {
    t19 = <Menu.Target><div className="flex items-center cursor-pointer mx-1">{t17}{t18}</div></Menu.Target>;
    $[30] = t17;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== profile) {
    t20 = <SAvatar profile={profile} size="sm" />;
    $[32] = profile;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== setModalOpen) {
    t21 = () => setModalOpen("open");
    $[34] = setModalOpen;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="opacity-60">Edit profile</div>;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  const t23 = profile?.name;
  let t24;
  if ($[37] !== t23) {
    t24 = <div className="font-semibold">{t23}</div>;
    $[37] = t23;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== t20 || $[40] !== t21 || $[41] !== t24) {
    t25 = <Menu.Item leftSection={t20} onClick={t21}>{t22}{t24}</Menu.Item>;
    $[39] = t20;
    $[40] = t21;
    $[41] = t24;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== advancedUI || $[44] !== qs || $[45] !== query) {
    t26 = advancedUI && <Menu.Item component={Link} leftSection={<FaHandSpock />} href={uri("/my-participation").mergeQuery(qs.queryStringRecord(query)).url}>My participation</Menu.Item>;
    $[43] = advancedUI;
    $[44] = qs;
    $[45] = query;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <HiLogout />;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== logOut) {
    t28 = <Menu.Item leftSection={t27} onClick={() => logOut()}>Log out</Menu.Item>;
    $[48] = logOut;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== t25 || $[51] !== t26 || $[52] !== t28) {
    t29 = <Menu.Dropdown className="space-y-1">{t25}{t26}{t28}</Menu.Dropdown>;
    $[50] = t25;
    $[51] = t26;
    $[52] = t28;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== t16 || $[55] !== t19 || $[56] !== t29) {
    t30 = <Menu width={t16} shadow="lg" position="bottom-end" trigger="click-hover" openDelay={100} closeDelay={400}>{t19}{t29}</Menu>;
    $[54] = t16;
    $[55] = t19;
    $[56] = t29;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] !== setModalOpen || $[59] !== user.is_anonymous) {
    t31 = user.is_anonymous && <Tooltip classNames={{
      tooltip: "max-w-[30em]"
    }} label="You are currently forecasting anonymously. Sign in to be able to log in again later and be associated with your forecasts and comments."><div><Button onClick={() => setModalOpen("anon-signup")}>Sign in</Button></div></Tooltip>;
    $[58] = setModalOpen;
    $[59] = user.is_anonymous;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  let t33;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <ShareButton />;
    t33 = <Notifys />;
    $[61] = t32;
    $[62] = t33;
  } else {
    t32 = $[61];
    t33 = $[62];
  }
  let t34;
  if ($[63] !== profile.license) {
    t34 = <LicenseBadge license={profile.license} />;
    $[63] = profile.license;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  if ($[65] !== t30 || $[66] !== t31 || $[67] !== t34) {
    t35 = <div id="top-bar-menu-portal" className="flex gap-2 items-center flex-row-reverse">{t15}{t30}{t31}{t32}{t33}{t34}</div>;
    $[65] = t30;
    $[66] = t31;
    $[67] = t34;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] !== t13 || $[70] !== t35) {
    t36 = <div className="flex items-center gap-2"><IconContext.Provider value={t13}>{t35}</IconContext.Provider></div>;
    $[69] = t13;
    $[70] = t35;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  let t37;
  if ($[72] !== modalOpen) {
    t37 = isProfileModal(modalOpen);
    $[72] = modalOpen;
    $[73] = t37;
  } else {
    t37 = $[73];
  }
  const t38 = modalOpen != "welcome";
  const t39 = modalOpen != "welcome";
  const t40 = modalOpen != "welcome";
  let t41;
  if ($[74] !== setModalOpen) {
    t41 = () => {
      setModalOpen(false);
    };
    $[74] = setModalOpen;
    $[75] = t41;
  } else {
    t41 = $[75];
  }
  const t42 = modalOpen == "welcome" ? "Welcome, select your avatar and name" : `Profile for ${profile.workspace.name}`;
  let t43;
  if ($[76] !== setModalOpen) {
    t43 = () => setModalOpen(false);
    $[76] = setModalOpen;
    $[77] = t43;
  } else {
    t43 = $[77];
  }
  let t44;
  if ($[78] !== t43 || $[79] !== tour) {
    t44 = <ProfileForm onSave={t43} tour={tour} />;
    $[78] = t43;
    $[79] = tour;
    $[80] = t44;
  } else {
    t44 = $[80];
  }
  let t45;
  if ($[81] !== t37 || $[82] !== t38 || $[83] !== t39 || $[84] !== t40 || $[85] !== t41 || $[86] !== t42 || $[87] !== t44) {
    t45 = <Modal opened={t37} closeOnEscape={t38} withCloseButton={t39} closeOnClickOutside={t40} onClose={t41} size="80em" keepMounted={false} title={t42}>{t44}</Modal>;
    $[81] = t37;
    $[82] = t38;
    $[83] = t39;
    $[84] = t40;
    $[85] = t41;
    $[86] = t42;
    $[87] = t44;
    $[88] = t45;
  } else {
    t45 = $[88];
  }
  let t46;
  if ($[89] !== modalOpen) {
    t46 = match(modalOpen).with("recovery", "anon-signup", _temp15).otherwise(_temp16);
    $[89] = modalOpen;
    $[90] = t46;
  } else {
    t46 = $[90];
  }
  let t47;
  if ($[91] !== noPrompt || $[92] !== profile.name || $[93] !== qs || $[94] !== setModalOpen) {
    t47 = () => {
      qs.setQuery({
        authDescription: undefined
      });
      setModalOpen(false);
      if (isEmpty(profile.name) && !noPrompt) {
        setModalOpen("welcome");
      }
    };
    $[91] = noPrompt;
    $[92] = profile.name;
    $[93] = qs;
    $[94] = setModalOpen;
    $[95] = t47;
  } else {
    t47 = $[95];
  }
  let t48;
  if ($[96] !== query.authDescription || $[97] !== t46 || $[98] !== t47) {
    t48 = <AuthModal showAction={t46} description={query.authDescription} onClose={t47} />;
    $[96] = query.authDescription;
    $[97] = t46;
    $[98] = t47;
    $[99] = t48;
  } else {
    t48 = $[99];
  }
  let t49;
  if ($[100] !== ref || $[101] !== t10 || $[102] !== t12 || $[103] !== t3 || $[104] !== t36 || $[105] !== t4 || $[106] !== t45 || $[107] !== t48 || $[108] !== t5 || $[109] !== t9) {
    t49 = <div ref={ref} id="top-bar" className={t3}>{t4}{t5}{t6}{t9}{t10}{t12}{t36}{t45}{t48}</div>;
    $[100] = ref;
    $[101] = t10;
    $[102] = t12;
    $[103] = t3;
    $[104] = t36;
    $[105] = t4;
    $[106] = t45;
    $[107] = t48;
    $[108] = t5;
    $[109] = t9;
    $[110] = t49;
  } else {
    t49 = $[110];
  }
  return t49;
};
const QuestionMenu = t0 => {
  const $ = _c(95);
  const {
    question_id,
    showTitle
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    questions,
    isLoading
  } = useQuestionsPageQuery(t1);
  let t2;
  if ($[1] !== questions) {
    t2 = {
      items: questions,
      showAllByDefault: true,
      disableHotkey: true
    };
    $[1] = questions;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    search,
    searchResults: searchResultsRaw,
    setSearch: rawSetSearch
  } = useQuestionSelect(t2);
  let t3;
  if ($[3] !== question_id || $[4] !== questions) {
    t3 = questions?.find(q => q.id == question_id);
    $[3] = question_id;
    $[4] = questions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const question = t3;
  const [opened, setOpened] = useState(false);
  const {
    workspace_id,
    readonly
  } = usePortal();
  const {
    isNavigating,
    startNavigation
  } = useNavigationLoading();
  const searchInputRef = useRef(null);
  let t4;
  if ($[6] !== rawSetSearch) {
    t4 = value => {
      rawSetSearch(value);
      if (value != "") {
        setOpened(true);
      }
    };
    $[6] = rawSetSearch;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const setSearch = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [["mod+k", () => {
      console.log("mod+k");
      setOpened(true);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }]];
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useHotkeys(t5);
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const [statusFilter, setStatusFilter] = useState(t6);
  let t7;
  if ($[10] !== statusFilter) {
    t7 = r => isEmpty(statusFilter) || statusFilter.includes(r.item.cfg.status);
    $[10] = statusFilter;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const filterItem = t7;
  const searchResults = searchResultsRaw?.filter(filterItem);
  const isOpen = opened && !isNavigating;
  let t8;
  if ($[12] !== setSearch || $[13] !== startNavigation) {
    t8 = question_id_0 => {
      startNavigation();
      setSearch("");
      setOpened(false);
    };
    $[12] = setSearch;
    $[13] = startNavigation;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== question_id || $[16] !== t8) {
    t9 = {
      onSelected: t8,
      question_id
    };
    $[15] = question_id;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const menuSectionCommon = t9;
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      key: "new-question",
      onOpen: () => {
        setOpened(false);
      }
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const newQModal = useNewQModal(t10);
  const T0 = Menu;
  const t11 = "max-content";
  const t12 = "lg";
  const t13 = "click";
  const t14 = 200;
  const t15 = 300;
  let t16;
  if ($[19] !== setSearch) {
    t16 = () => {
      setSearch("");
      setOpened(false);
    };
    $[19] = setSearch;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const t17 = true;
  const t18 = false;
  const t19 = "bottom";
  const t20 = 2;
  let t21;
  if ($[21] !== isNavigating) {
    t21 = open => {
      if (isNavigating) {
        return;
      }
      setOpened(open);
    };
    $[21] = isNavigating;
    $[22] = t21;
  } else {
    t21 = $[22];
  }
  const t22 = question_id ?? "";
  let t23;
  if ($[23] !== question_id || $[24] !== showTitle) {
    t23 = twMerge("text-lg font-semibold ml-2 flex items-center gap-1 cursor-pointer sh min-w-0 ", (!showTitle || isNullOrEmpty(question_id)) && "text-primary");
    $[23] = question_id;
    $[24] = showTitle;
    $[25] = t23;
  } else {
    t23 = $[25];
  }
  let t24;
  if ($[26] !== isLoading || $[27] !== isNavigating || $[28] !== question?.cfg || $[29] !== question_id || $[30] !== showTitle) {
    let t25;
    if ($[32] !== question?.cfg || $[33] !== question_id || $[34] !== showTitle) {
      t25 = () => <><TruncatedText>{!showTitle || isNullOrEmpty(question_id) ? "Questions" : question?.cfg.title ?? ""}</TruncatedText><HiSelector size={14} className="shrink-0" /></>;
      $[32] = question?.cfg;
      $[33] = question_id;
      $[34] = showTitle;
      $[35] = t25;
    } else {
      t25 = $[35];
    }
    t24 = match({
      question_id,
      isLoading,
      showTitle,
      isNavigating
    }).with({
      isLoading: true
    }, {
      isNavigating: true
    }, _temp17).otherwise(t25);
    $[26] = isLoading;
    $[27] = isNavigating;
    $[28] = question?.cfg;
    $[29] = question_id;
    $[30] = showTitle;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  let t25;
  if ($[36] !== t22 || $[37] !== t23 || $[38] !== t24) {
    t25 = <Menu.Target><div data-item-id={t22} className={t23}>{t24}</div></Menu.Target>;
    $[36] = t22;
    $[37] = t23;
    $[38] = t24;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  const t26 = Menu;
  const t27 = "max-h-[80vh] min-h-[min(30em,80vh)] overflow-y-auto flex flex-col  gap-3 !w-[min(100vw-2em,80em)] grow !p-0";
  const t28 = "flex flex-col gap-3 px-2 pb-6";
  let t29;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      root: "w-content self-start",
      content: "flex items-start justify-between"
    };
    $[40] = t29;
  } else {
    t29 = $[40];
  }
  let t30;
  if ($[41] !== workspace_id) {
    t30 = nav.questions.getUrl({
      workspace: workspace_id
    });
    $[41] = workspace_id;
    $[42] = t30;
  } else {
    t30 = $[42];
  }
  let t31;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <MdHome className="icon-text" />;
    $[43] = t31;
  } else {
    t31 = $[43];
  }
  let t32;
  if ($[44] !== t30.url) {
    t32 = <Link className="font-semibold text-[1.1em] px-2 group-data-[sticking=true]:hidden block" href={t30.url}>{t31} Home</Link>;
    $[44] = t30.url;
    $[45] = t32;
  } else {
    t32 = $[45];
  }
  let t33;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = status => {
      setStatusFilter(status);
    };
    $[46] = t33;
  } else {
    t33 = $[46];
  }
  let t34;
  if ($[47] !== questions) {
    t34 = questions ? mapValues(groupBy(questions, _temp18), _temp19) : {};
    $[47] = questions;
    $[48] = t34;
  } else {
    t34 = $[48];
  }
  let t35;
  if ($[49] !== statusFilter || $[50] !== t34) {
    t35 = <QuestionStatusFilter value={statusFilter} onChange={t33} showClear={true} counts={t34} hideZeros={true} withinPortal={false} />;
    $[49] = statusFilter;
    $[50] = t34;
    $[51] = t35;
  } else {
    t35 = $[51];
  }
  let t36;
  if ($[52] !== setSearch) {
    t36 = e => {
      setSearch(e.currentTarget.value);
    };
    $[52] = setSearch;
    $[53] = t36;
  } else {
    t36 = $[53];
  }
  let t37;
  if ($[54] !== search || $[55] !== setSearch) {
    t37 = search != "" ? <CloseButton onClick={() => setSearch("")} /> : <KbdComboLabel className="mx-1" combo={{
      mods: ["Meta"],
      key: "k"
    }} />;
    $[54] = search;
    $[55] = setSearch;
    $[56] = t37;
  } else {
    t37 = $[56];
  }
  let t38;
  if ($[57] !== search || $[58] !== t36 || $[59] !== t37) {
    t38 = <TextInput ref={searchInputRef} radius="md" value={search} w="20em" onChange={t36} placeholder="Search questions" rightSection={t37} />;
    $[57] = search;
    $[58] = t36;
    $[59] = t37;
    $[60] = t38;
  } else {
    t38 = $[60];
  }
  let t39;
  if ($[61] !== newQModal || $[62] !== readonly) {
    t39 = !readonly && <NewQuestionButton {...newQModal.buttonProps} className="block group-data-[sticking=true]:hidden" />;
    $[61] = newQModal;
    $[62] = readonly;
    $[63] = t39;
  } else {
    t39 = $[63];
  }
  let t40;
  if ($[64] !== t32 || $[65] !== t35 || $[66] !== t38 || $[67] !== t39) {
    t40 = <ActionCard classNames={t29}><div className="flex items-center gap-2 flex-wrap">{t32}{t35}{t38}{t39}</div></ActionCard>;
    $[64] = t32;
    $[65] = t35;
    $[66] = t38;
    $[67] = t39;
    $[68] = t40;
  } else {
    t40 = $[68];
  }
  let t41;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="absolute top-1 right-1"><CloseButton className="self-start" onClick={() => setOpened(false)} /></div>;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  let t42;
  if ($[70] !== menuSectionCommon) {
    t42 = t43 => {
      const {
        searchResults: searchResults_0
      } = t43;
      return <QuestionMenuSection name="Search results" items={searchResults_0} {...menuSectionCommon} />;
    };
    $[70] = menuSectionCommon;
    $[71] = t42;
  } else {
    t42 = $[71];
  }
  let t43;
  if ($[72] !== filterItem || $[73] !== menuSectionCommon || $[74] !== searchResultsRaw) {
    t43 = qs_0 => {
      const recent = take(sortBy(searchResultsRaw.filter(_temp22), [_temp23, "desc"]), 6).filter(filterItem);
      const [active, inactive] = partition(qs_0, _temp24);
      return <>{notNullOrEmpty(recent) && <QuestionMenuSection name="Recently Opened" items={recent} {...menuSectionCommon} />}{notNullOrEmpty(active) && <QuestionMenuSection name="Active" items={active} {...menuSectionCommon} />}{notNullOrEmpty(inactive) && <QuestionMenuSection name="Inactive" items={inactive} {...menuSectionCommon} />}</>;
    };
    $[72] = filterItem;
    $[73] = menuSectionCommon;
    $[74] = searchResultsRaw;
    $[75] = t43;
  } else {
    t43 = $[75];
  }
  const t44 = match({
    search,
    searchResults
  }).with({
    searchResults: P.nullish
  }, _temp21).with({
    search: P.not("")
  }, t42).with({
    searchResults: P.not(P.nullish).select()
  }, t43).otherwise(_temp25);
  let t45;
  if ($[76] !== t40 || $[77] !== t41 || $[78] !== t44) {
    t45 = <div className={t28}>{t40}{t41}{t44}</div>;
    $[76] = t40;
    $[77] = t41;
    $[78] = t44;
    $[79] = t45;
  } else {
    t45 = $[79];
  }
  let t46;
  if ($[80] !== t26.Dropdown || $[81] !== t45) {
    t46 = <t26.Dropdown className={t27}>{t45}</t26.Dropdown>;
    $[80] = t26.Dropdown;
    $[81] = t45;
    $[82] = t46;
  } else {
    t46 = $[82];
  }
  let t47;
  if ($[83] !== T0 || $[84] !== isOpen || $[85] !== t16 || $[86] !== t21 || $[87] !== t25 || $[88] !== t46) {
    t47 = <T0 width={t11} shadow={t12} trigger={t13} openDelay={t14} closeDelay={t15} opened={isOpen} onDismiss={t16} withArrow={t17} withinPortal={t18} position={t19} offset={t20} onChange={t21}>{t25}{t46}</T0>;
    $[83] = T0;
    $[84] = isOpen;
    $[85] = t16;
    $[86] = t21;
    $[87] = t25;
    $[88] = t46;
    $[89] = t47;
  } else {
    t47 = $[89];
  }
  let t48;
  if ($[90] !== newQModal) {
    t48 = <NewQuestionModal {...newQModal} />;
    $[90] = newQModal;
    $[91] = t48;
  } else {
    t48 = $[91];
  }
  let t49;
  if ($[92] !== t47 || $[93] !== t48) {
    t49 = <>{t47}{t48}</>;
    $[92] = t47;
    $[93] = t48;
    $[94] = t49;
  } else {
    t49 = $[94];
  }
  return t49;
};
const QuestionMenuSection = t0 => {
  const $ = _c(15);
  const {
    name,
    items,
    onSelected,
    question_id
  } = t0;
  const {
    profilesUI,
    workspace_id
  } = usePortal();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      label: "uppercase text-sm text-gray-500/50 font-extrabold rounded-lg group-data-[sticking=true]:shadow-md group-data-[sticking=true]:bg-[var(--mantine-color-body)]",
      hr: "group-data-[sticking=true]:hidden"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== name) {
    t2 = <Sticky><HrText classNames={t1}>{name}</HrText></Sticky>;
    $[1] = name;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      column: "gap-3"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== items || $[5] !== onSelected || $[6] !== profilesUI || $[7] !== question_id || $[8] !== workspace_id) {
    t4 = items == null ? range(0, 3).map(_temp26) : items.map((t5, index) => {
      const {
        item: q,
        matches
      } = t5;
      return <Menu.Item key={q.id} component={Link} onClick={() => {
        onSelected?.(q.id);
      }} href={nav.question.getUrl({
        workspace: workspace_id,
        question_id: q.id
      }).url} data-highlight={q.id == question_id} classNames={{
        itemLabel: "flex flex-col justify-start items-start gap-1 text-[--mantine-color-text] py-1",
        item: "!items-start"
      }} className="hover:bg-gray-50 !rounded-lg cursor-pointer data-[highlight=true]:bg-primary/10  max-w-[50em]"><QuestionHeader classNames={{
          title: "!text-gray-700 text-[1rem]"
        }} title={matches ? <MarkText marks={matches?.find(_temp27)?.indices}>{q.cfg.title}</MarkText> : q.cfg.title} subTitle={matches ? <MarkText marks={matches?.find(_temp28)?.indices}>{q.cfg.subTitle}</MarkText> : q.cfg.subTitle} /><div className="flex items-center gap-4"><QuestionTileItems statusItem={{
            md: questionStatusMd[q.cfg.status],
            value: q.cfg.status
          }} {...pick(q, ["forecasters_total", "forecasts_from", "forecasts_to", "created_at"])} profile={profilesUI?.[q.cfg.owner ?? q.created_by]} /></div>{notNullOrEmpty(matches) && q.cfg.attachments && <QuestionAttachmentList attachments={Object.entries(q.cfg.attachments).map(_temp29).filter(Boolean).map(a_0 => ({
          ...a_0,
          name: <MarkText marks={matches?.find(_temp30)?.indices}>{a_0.name}</MarkText>
        }))} />}{notNullOrEmpty(matches) && q.subquestions && <QuestionSubquestions question_id={q.id} subquestions={q.subquestions.map((sq, i_0) => ({
          ...sq,
          title: <MarkText marks={matches?.find(m_2 => m_2.key == "subquestions.title" && m_2.refIndex == i_0)?.indices}>{sq.title}</MarkText>
        }))} />}</Menu.Item>;
    });
    $[4] = items;
    $[5] = onSelected;
    $[6] = profilesUI;
    $[7] = question_id;
    $[8] = workspace_id;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <Masonry colWidthBasis={400} className="gap-3" classNames={t3}>{t4}</Masonry>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t5) {
    t6 = <div className="relative">{t2}{t5}</div>;
    $[12] = t2;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
const InlineMenu = t0 => {
  const $ = _c(4);
  const {
    menuItems
  } = t0;
  let t1;
  if ($[0] !== menuItems) {
    t1 = menuItems.map(_temp31);
    $[0] = menuItems;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className="flex h-full font-semibold ">{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const InlineMenuItem = ({
  item
}: {
  item: MenuItem;
}) => {
  const isDropdown = notNullOrEmpty(item.subItems);
  const props = {
    className: twMerge(`flex h-full items-center px-3 gap-4 text-primary cursor-pointer relative`, item.isActive ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1 after:bg-primary after:rounded-full' : ''),
    children: <div className='flex items-center gap-2'>
        {item.Icon && <item.Icon />}
        {item.title}
        {isDropdown && <HiChevronDown size={14} />}
      </div>
  };
  return match(item.subItems).when(s => notNullOrEmpty(s), subItems => {
    const {
      profile
    } = usePortal();
    const isMultiCategory = unique(subItems.map(s_0 => s_0.category)).length > 1;
    return <Menu key={item.name} width='max-content' shadow='md' trigger='click-hover' openDelay={100} closeDelay={400}>
            <Menu.Target>
              <div {...props} />
            </Menu.Target>
            <Menu.Dropdown>
              <IconContext.Provider value={{
          size: '20px',
          color: 'var(--mantine-color-gray-5)'
        }}>
                <MasonryGrid className='p-2 max-w-[20em]'>
                  {groupMap(subItems, s_1 => s_1.category ?? profile.workspace.name, (cat, s_2) => <div key={cat}>
                        {isMultiCategory && <b className='font-bold opacity-40'>{upperFirst(cat)}</b>}
                        {s_2.map(s_3 => <SubMenuItem key={s_3.id} item={s_3} showDescription>
                            {s_3.id == 'users' && <div className=' space-y-1'>
                                {adminUserSections.map(s_4 => <Link key={s_4.name} href={item.url.mergeQuery({
                    tab: 'users',
                    adminSections: s_4.name
                  }).url} className='block text-sm text-primary hover:underline'>
                                    {s_4.menuTitle}
                                  </Link>)}
                              </div>}
                          </SubMenuItem>)}
                      </div>)}
                </MasonryGrid>
              </IconContext.Provider>
            </Menu.Dropdown>
          </Menu>;
  }).otherwise(() => <Link href={item.url.url} {...props}></Link>);
};
const SubMenuItem = t0 => {
  const $ = _c(19);
  const {
    item,
    showDescription,
    children
  } = t0;
  const t1 = item.id;
  let t2;
  if ($[0] !== item.id || $[1] !== item.url) {
    t2 = item.url.mergeQuery({
      tab: item.id
    });
    $[0] = item.id;
    $[1] = item.url;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== item.Icon) {
    t3 = item.Icon && <item.Icon />;
    $[3] = item.Icon;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== item.title || $[6] !== t3) {
    t4 = <div className="flex items-center gap-2 text-md font-semibold text-primary">{t3}{item.title}</div>;
    $[5] = item.title;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== item.description || $[9] !== showDescription) {
    t5 = showDescription && <div className="text-sm opacity-60">{item.description}</div>;
    $[8] = item.description;
    $[9] = showDescription;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== children || $[12] !== t4 || $[13] !== t5) {
    t6 = <div className="flex flex-col gap-2">{t4}{t5}{children}</div>;
    $[11] = children;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== item.id || $[16] !== t2.url || $[17] !== t6) {
    t7 = <Menu.Item key={t1} component={Link} href={t2.url} className="!p-2 hover:bg-gray-50 rounded-lg unstyled">{t6}</Menu.Item>;
    $[15] = item.id;
    $[16] = t2.url;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};
const HamburgerMenu = t0 => {
  const $ = _c(16);
  const {
    menuItems
  } = t0;
  const {
    query
  } = useQueryString(zQuery);
  let t1;
  if ($[0] !== query) {
    t1 = Object.fromEntries(Object.entries(query).map(_temp32));
    $[0] = query;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  const {
    workspace_id
  } = usePortal();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Menu.Target><ActionIcon size="lg" className="flex items-center justify-center"><HiMenu size={20} /></ActionIcon></Menu.Target>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== workspace_id) {
    t3 = nav.questions.getUrl({
      workspace: workspace_id
    });
    $[3] = workspace_id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <MdHome />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3.url) {
    t5 = <Menu.Item className="!py-2 !px-4 font-semibold" href={t3.url} component={Link} leftSection={t4}>Home</Menu.Item>;
    $[6] = t3.url;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== menuItems || $[9] !== queryParams) {
    let t7;
    if ($[11] !== queryParams) {
      t7 = item => <HamburgerMenuItem key={item.name} item={item} queryParams={queryParams} />;
      $[11] = queryParams;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    t6 = menuItems.map(t7);
    $[8] = menuItems;
    $[9] = queryParams;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <Menu position="bottom-end" shadow="md">{t2}<Menu.Dropdown className="!p-2">{t5}{t6}</Menu.Dropdown></Menu>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
const HamburgerMenuItem = t0 => {
  const $ = _c(6);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item.Icon || $[1] !== item.name || $[2] !== item.subItems || $[3] !== item.title || $[4] !== item.url) {
    t1 = item.subItems ? <><div className="!py-2 !px-4 font-semibold text-gray-600">{item.title}</div>{item.subItems.map(subItem => <HamburgerSubMenuItem key={subItem.id} item={subItem} basePath={item.name} />)}</> : <Menu.Item className="!py-2 !px-4 font-semibold" href={item.url.url} component={Link} leftSection={item.Icon && <item.Icon />}>{item.title}</Menu.Item>;
    $[0] = item.Icon;
    $[1] = item.name;
    $[2] = item.subItems;
    $[3] = item.title;
    $[4] = item.url;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const HamburgerSubMenuItem = t0 => {
  const $ = _c(11);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item.Icon) {
    t1 = item.Icon && <item.Icon />;
    $[0] = item.Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.title) {
    t2 = <span>{item.title}</span>;
    $[2] = item.title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center gap-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== item.id || $[8] !== item.url.url || $[9] !== t3) {
    t4 = <Menu.Item key={item.id} component={Link} href={item.url.url} className="!py-2 !px-4 !pl-8 font-semibold">{t3}</Menu.Item>;
    $[7] = item.id;
    $[8] = item.url.url;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const LicenseBadge = t0 => {
  const $ = _c(6);
  const {
    license
  } = t0;
  const {
    workspace_id,
    readonly
  } = usePortal();
  let t1;
  if ($[0] !== readonly || $[1] !== workspace_id) {
    t1 = l => {
      const expiresInDays = doSafe(l.expires, _temp33);
      const expiresCat = expiresInDays == null || l.plan != "trial" && !env.NEXT_PUBLIC_SELF_HOST ? null : expiresInDays < 0 ? "red" : expiresInDays < 7 ? "orange" : "green";
      const getStatusTitle = () => match(l.plan).with("trial", _temp34).otherwise(() => match(expiresCat).with("red", _temp35).with("orange", _temp36).otherwise(_temp37));
      const getExpiryText = () => match(expiresCat).with("red", () => <><span>Expired </span><span className="font-semibold">{Math.round(Math.abs(expiresInDays))} days ago</span></>).with("orange", "green", () => <><span>Expires in </span><span className="font-semibold">{Math.round(Math.abs(expiresInDays))} days</span></>).with(P.nullish, _temp38).exhaustive();
      const statusTitle = getStatusTitle();
      const expiryText = getExpiryText();
      const tooltipContent = <div className="flex flex-col gap-2 p-2"><h3>{statusTitle}</h3>{expiryText && <div className="text-sm">{expiryText}</div>}{readonly && <div className="text-sm">This workspace is read only</div>}{l.plan == "trial" && <Button component={Link} href={nav.admin.getUrl({
          tab: "subscription",
          workspace: workspace_id
        }).url} variant="filled" className="w-full unstyled mt-2">Upgrade subscription</Button>}</div>;
      return <Tip label={tooltipContent}>{match(l.plan).with("trial", () => <Badge className="cursor-pointer" color={expiresCat ?? "green"}>{statusTitle}</Badge>).otherwise(() => match(expiresCat).with("red", () => <Badge color="red">{statusTitle}</Badge>).with("orange", () => <Badge color="orange">{statusTitle}</Badge>).otherwise(_temp39))}</Tip>;
    };
    $[0] = readonly;
    $[1] = workspace_id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== license || $[4] !== t1) {
    t2 = doSafe(license, t1);
    $[3] = license;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
function _temp() {
  return false;
}
function _temp2() {
  return true;
}
function _temp3() {
  return true;
}
function _temp4() {
  return MdHome;
}
function _temp5() {
  return HiCog;
}
function _temp6() {
  return HiLightBulb;
}
function _temp7() {
  return HiClipboardCheck;
}
function _temp8() {}
function _temp9() {}
function _temp10() {
  Crisp.chat.hide();
}
function _temp11() {
  if (!Crisp.chat.isChatOpened()) {
    Crisp.chat.open();
  }
  if (!Crisp.chat.isVisible()) {
    Crisp.chat.show();
  }
}
function _temp12() {
  if (env.NEXT_PUBLIC_CRISP_WEBSITE_ID) {
    Crisp.load();
    Crisp.chat.hide();
    Crisp.chat.onChatClosed(_temp10);
    Crisp.message.onMessageReceived(_temp11);
  }
}
function _temp13(name) {
  return <Badge className="hidden md:block mx-2" color="orange" variant="light">{name}</Badge>;
}
function _temp14() {
  if (!Crisp.chat.isChatOpened()) {
    Crisp.chat.open();
  }
  if (!Crisp.chat.isVisible()) {
    Crisp.chat.show();
  }
}
function _temp15(a) {
  return a;
}
function _temp16() {}
function _temp17() {
  return <Loader />;
}
function _temp18(q_0) {
  return q_0.cfg.status;
}
function _temp19(qs) {
  return qs.length;
}
function _temp20(i) {
  return <Skeleton key={i} h="2em" w="15em" />;
}
function _temp21() {
  return <>{range(0, 3).map(_temp20)}</>;
}
function _temp22(q_1) {
  return q_1.item.last_viewed_at && q_1.item.last_viewed_at.diffNow().as("days") > -7;
}
function _temp23(q_2) {
  return q_2.item.last_viewed_at.toSeconds();
}
function _temp24(q_3) {
  return questionStatusMd[q_3.item.cfg.status].active ?? false;
}
function _temp25() {
  return <></>;
}
function _temp26(i) {
  return <Skeleton key={i} h="2em" w="15em" />;
}
function _temp27(m) {
  return m.key == "cfg.title";
}
function _temp28(m_0) {
  return m_0.key == "cfg.subTitle";
}
function _temp29(t0) {
  const [path, a] = t0;
  return a ? {
    path,
    ...a
  } : undefined;
}
function _temp30(m_1) {
  return m_1.key == "attachments.name";
}
function _temp31(item) {
  return <InlineMenuItem key={item.name} item={item} />;
}
function _temp32(t0) {
  const [key, value] = t0;
  return [key, value?.toString()];
}
function _temp33(e) {
  return D.fromISO(e).diffNow().as("days");
}
function _temp34() {
  return "Trial";
}
function _temp35() {
  return "Subscription expired";
}
function _temp36() {
  return "Subscription expiring";
}
function _temp37() {
  return "Active subscription";
}
function _temp38() {
  return null;
}
function _temp39() {
  return null;
}